import {
  $applyNodeReplacement,
  TextNode,
  type EditorConfig,
  type LexicalNode,
  type LexicalUpdateJSON,
  type NodeKey,
  type SerializedTextNode,
  type Spread,
} from 'lexical';

/**
 * A `@mention` in the composer.
 *
 * ─── WHY A TextNode SUBCLASS, NOT A DECORATOR ────────────────────────────────
 *
 * A DecoratorNode would let us render a real React chip, but it takes the
 * mention out of the text stream: selection, backspace and copy all have to be
 * re-implemented around it, and every one of those is a place a mention can end
 * up half-deleted. A TextNode subclass keeps Lexical's own selection model, and
 * a class name on the DOM element is enough to make it *look* like a chip.
 *
 * ─── WHY token MODE ──────────────────────────────────────────────────────────
 *
 * `setMode('token')` makes the node atomic: it deletes as a unit and cannot be
 * typed into. That is not cosmetic. This node carries a `userId` alongside the
 * text, and the two must agree — a mention reading "@Dav" that still points at
 * David's Entra id would notify somebody whose name is not on screen. Token
 * mode is what makes "edit the text of a mention" impossible rather than merely
 * unlikely.
 */

/**
 * Who a mention points at: one person, or the whole conversation.
 *
 * A closed union rather than two optional ids, so "neither" and "both" are not
 * states this node can be in — the serializer downstream has to pick one shape
 * and there is no sensible answer for a mention that is somehow both.
 */
export type MentionTarget =
  | { kind: 'user'; userId: string }
  | { kind: 'conversation'; conversationId: string };

export type SerializedMentionNode = Spread<
  { mentionTarget: MentionTarget },
  SerializedTextNode
>;

export class MentionNode extends TextNode {
  /** Who this mention notifies. */
  __target: MentionTarget;

  static getType(): string {
    return 'teams-mention';
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(node.__text, node.__target, node.__key);
  }

  constructor(text: string, target: MentionTarget, key?: NodeKey) {
    super(text, key);
    this.__target = target;
  }

  getTarget(): MentionTarget {
    return this.getLatest().__target;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = super.createDOM(config);
    // Styled through `composerContentSx` so it reads the MUI theme — house rule
    // is no raw hex and no magic px in a component.
    element.className = 'teams-composer-mention';
    // Read as one thing by a screen reader rather than as loose punctuation
    // followed by a name.
    applyTargetAttribute(element, this.__target);
    return element;
  }

  updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
    const updated = super.updateDOM(prevNode, dom, config);
    if (targetKey(prevNode.__target) !== targetKey(this.__target)) {
      applyTargetAttribute(dom, this.__target);
    }
    dom.className = 'teams-composer-mention';
    return updated;
  }

  /**
   * Round-trips through JSON. Without this the target survives an in-memory
   * edit but is lost the moment the document is serialized and restored — and
   * a mention with no id is just text that looks like a mention.
   */
  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    return $createMentionNode(serializedNode.text, serializedNode.mentionTarget).updateFromJSON(
      serializedNode,
    );
  }

  updateFromJSON(serializedNode: LexicalUpdateJSON<SerializedMentionNode>): this {
    const self = super.updateFromJSON(serializedNode);
    // `super` restores the text and formats; the target is ours to carry.
    const writable = self.getWritable();
    writable.__target = serializedNode.mentionTarget;
    return writable;
  }

  exportJSON(): SerializedMentionNode {
    return {
      ...super.exportJSON(),
      mentionTarget: this.getTarget(),
    };
  }

  /**
   * Not a text entity: Lexical's text-entity machinery re-derives a node from
   * its surrounding text, which is exactly what must not happen to something
   * whose identity lives outside the text.
   */
  isTextEntity(): boolean {
    return false;
  }

  /** Nothing may be typed into or immediately after the token. */
  canInsertTextBefore(): boolean {
    return false;
  }

  canInsertTextAfter(): boolean {
    return false;
  }
}

/** Stable identity of a target, for change detection. */
function targetKey(target: MentionTarget): string {
  return target.kind === 'user' ? `user:${target.userId}` : `conversation:${target.conversationId}`;
}

function applyTargetAttribute(element: HTMLElement, target: MentionTarget): void {
  element.removeAttribute('data-teams-mention-user-id');
  element.removeAttribute('data-teams-mention-conversation-id');
  if (target.kind === 'user') {
    element.setAttribute('data-teams-mention-user-id', target.userId);
  } else {
    element.setAttribute('data-teams-mention-conversation-id', target.conversationId);
  }
}

export function $createMentionNode(text: string, target: MentionTarget): MentionNode {
  const node = new MentionNode(text, target);
  // Atomic — see the note above. Set here rather than in the constructor so
  // `clone` (which goes through the constructor) cannot skip it: Lexical
  // copies `__mode` across in `afterCloneFrom`.
  node.setMode('token');
  return $applyNodeReplacement(node);
}

export function $isMentionNode(node: LexicalNode | null | undefined): node is MentionNode {
  return node instanceof MentionNode;
}
